/**
 * Discovery Assistant Store
 *
 * Manages state for the AI-guided discovery workflow including:
 * - Session management
 * - Conversation history
 * - Answer tracking with quality assessment
 * - Live validation state
 */

import { create } from 'zustand'
import {
  discoveryApi,
  workflowApi,
  type DiscoverySession,
  type DiscoveryAnswer,
  type DiscoverySource,
  type WorkflowStep,
  type WorkflowPhase,
} from '@/lib/api/client'
import {
  DISCOVERY_QUESTIONS,
  PHASE_CONFIG,
  QUALITY_CONFIG,
  parsePollFromContent,
  parseMultiStepFromContent,
  stripInteractiveBlocks,
  type AnswerQuality,
  type QualityAssessment,
  type AISuggestion,
} from '@/lib/ai/conversationAI'

// Poll/Question data structures for interactive messaging
export interface PollOption {
  id: string
  label: string
  description?: string
  isRecommended?: boolean
  value: string
}

export interface PollData {
  pollId: string
  question: string
  type: 'single' | 'multiple'
  options: PollOption[]
  selectedValues?: string[]
  allowOther?: boolean
  otherText?: string
}

export interface MultiStepQuestionData {
  multiStepId: string
  title: string
  steps: Array<{
    id: string
    label: string
    question: string
    type: 'single' | 'multiple'
    options: PollOption[]
    selectedValues?: string[]
  }>
  currentStepIndex: number
}

// Local message type for UI (includes pending state)
export interface LocalMessage {
  id: string
  role: 'assistant' | 'user' | 'system'
  content: string
  messageType: string // 'question' | 'answer' | 'transition' | 'follow_up' | 'poll' | 'multi_step_question'
  questionNumber?: string
  timestamp: Date
  isPending?: boolean
  isStreaming?: boolean
  // NEW: Interactive data
  pollData?: PollData
  multiStepData?: MultiStepQuestionData
}

// Live validation state while typing
export interface ValidationState {
  isValidating: boolean
  assessment: QualityAssessment | null
  suggestions: AISuggestion[]
  ghostText: string | null
}

// Coach workflow state (for workflow-driven AI coaching)
export interface CoachAssessment {
  quality: string
  confidence: number
  feedback: string
  suggestions?: string[]
}

export interface CoachMCQOption {
  id: string
  label: string
  description?: string
}

export interface CoachExtraction {
  entities: Array<{ type: string; label: string; description?: string }>
  insights: string[]
  keyPoints: string[]
}

export interface CoachWorkflowState {
  isEnabled: boolean
  currentStepKey: string
  currentStepNumber: number
  phase: 'idle' | 'streaming' | 'awaiting_input' | 'complete'
  completedSteps: string[]
  lastAssessment?: CoachAssessment
  lastMCQOptions?: CoachMCQOption[]
  lastExtraction?: CoachExtraction
  streamingText: string
}

interface DiscoveryAssistantState {
  // Session data
  session: DiscoverySession | null
  messages: LocalMessage[]
  answers: Map<string, DiscoveryAnswer>
  sources: DiscoverySource[]

  // Workflow data (from database)
  workflowSteps: WorkflowStep[]
  workflowPhases: WorkflowPhase[]
  isWorkflowLoaded: boolean

  // UI state
  isLoading: boolean
  isSending: boolean
  error: string | null

  // Current input
  inputText: string
  validation: ValidationState

  // Coach workflow state
  coachWorkflow: CoachWorkflowState

  // Computed helpers
  currentQuestion: typeof DISCOVERY_QUESTIONS[number] | null
  currentPhase: typeof PHASE_CONFIG[keyof typeof PHASE_CONFIG] | null
  progress: {
    completed: number
    total: number
    percentage: number
  }

  // Actions
  setInputText: (text: string) => void
  setValidation: (validation: Partial<ValidationState>) => void

  // Workflow actions
  loadWorkflow: () => Promise<void>
  getCurrentStep: () => WorkflowStep | null

  // API actions
  loadSession: (accountId: string, projectId: string, sessionId: string) => Promise<void>
  createSession: (accountId: string, projectId: string, data: {
    title?: string
    mode?: 'quick' | 'deep'
    ideaText?: string
  }) => Promise<DiscoverySession>
  sendMessage: (accountId: string, projectId: string, content: string) => Promise<void>
  saveAnswer: (accountId: string, projectId: string, questionNumber: string, answer: string, quality?: AnswerQuality) => Promise<void>
  advanceToNext: (accountId: string, projectId: string) => Promise<boolean>
  addSource: (accountId: string, projectId: string, source: {
    sourceType: 'document' | 'url' | 'text'
    title: string
    content?: string
    analyzedFacets?: string
  }) => Promise<void>

  // Local actions
  addLocalMessage: (message: Omit<LocalMessage, 'id' | 'timestamp'>) => void
  updateStreamingMessage: (content: string) => void
  clearSession: () => void

  // Poll actions
  submitPollAnswer: (
    accountId: string,
    projectId: string,
    pollId: string,
    selectedValues: string[],
    otherText?: string
  ) => Promise<void>
  submitMultiStepAnswer: (
    accountId: string,
    projectId: string,
    multiStepId: string,
    allAnswers: Record<string, string[]>
  ) => Promise<void>

  // Coach workflow actions
  setCoachWorkflowEnabled: (enabled: boolean) => void
  updateCoachWorkflowState: (state: Partial<CoachWorkflowState>) => void
  setCoachStreamingText: (text: string) => void
  setCoachAssessment: (assessment: CoachAssessment) => void
  addCoachMCQOption: (option: CoachMCQOption) => void
  clearCoachMCQOptions: () => void
  setCoachExtraction: (extraction: CoachExtraction) => void
  advanceCoachStep: (nextStepKey: string, nextStepNumber: number) => void
  resetCoachWorkflow: () => void
}

export const useDiscoveryAssistantStore = create<DiscoveryAssistantState>((set, get) => ({
  // Initial state
  session: null,
  messages: [],
  answers: new Map(),
  sources: [],

  // Workflow data
  workflowSteps: [],
  workflowPhases: [],
  isWorkflowLoaded: false,

  isLoading: false,
  isSending: false,
  error: null,

  inputText: '',
  validation: {
    isValidating: false,
    assessment: null,
    suggestions: [],
    ghostText: null,
  },

  // Coach workflow initial state
  coachWorkflow: {
    isEnabled: false,
    currentStepKey: 'understanding-context',
    currentStepNumber: 1,
    phase: 'idle',
    completedSteps: [],
    streamingText: '',
  },

  // Computed (these are recalculated on access)
  get currentQuestion() {
    const session = get().session
    if (!session) return null
    const qNum = parseInt(session.currentQuestion)
    return DISCOVERY_QUESTIONS[qNum - 1] || null
  },

  get currentPhase() {
    const session = get().session
    if (!session) return null
    return PHASE_CONFIG[session.currentPhase] || null
  },

  get progress() {
    const session = get().session
    if (!session) return { completed: 0, total: 12, percentage: 0 }
    const completed = session.completedQuestions.length
    return {
      completed,
      total: 12,
      percentage: Math.round((completed / 12) * 100),
    }
  },

  // Input actions
  setInputText: (text) => set({ inputText: text }),

  setValidation: (validation) => set((state) => ({
    validation: { ...state.validation, ...validation },
  })),

  // Workflow actions
  loadWorkflow: async () => {
    try {
      const data = await workflowApi.getDiscoveryWorkflow()
      set({
        workflowSteps: data.steps,
        workflowPhases: data.phases,
        isWorkflowLoaded: true,
      })
    } catch (error) {
      console.error('Failed to load workflow:', error)
      // Fallback: workflow will remain empty, use DISCOVERY_QUESTIONS
    }
  },

  getCurrentStep: () => {
    const { session, workflowSteps } = get()
    if (!session) return null
    const qNum = parseInt(session.currentQuestion)
    return workflowSteps.find(s => s.order === qNum) || null
  },

  // API actions
  loadSession: async (accountId, projectId, sessionId) => {
    set({ isLoading: true, error: null })
    try {
      const data = await discoveryApi.getSession(accountId, projectId, sessionId)

      // Convert API messages to local format with poll parsing
      const localMessages: LocalMessage[] = data.messages.map(m => {
        const baseMessage: LocalMessage = {
          id: m.id,
          role: m.role,
          content: m.content,
          messageType: m.messageType,
          questionNumber: m.questionNumber || undefined,
          timestamp: new Date(m.createdAt),
        }

        // Auto-detect poll data in assistant messages
        if (m.role === 'assistant') {
          const pollData = parsePollFromContent(m.content)
          if (pollData) {
            baseMessage.messageType = 'poll'
            baseMessage.pollData = pollData
            // Strip JSON block from content for cleaner display
            baseMessage.content = stripInteractiveBlocks(m.content)
          }

          const multiStepData = parseMultiStepFromContent(m.content)
          if (multiStepData) {
            baseMessage.messageType = 'multi_step_question'
            baseMessage.multiStepData = multiStepData
            // Strip JSON block from content
            baseMessage.content = stripInteractiveBlocks(m.content)
          }
        }

        return baseMessage
      })

      // Convert answers to Map
      const answersMap = new Map<string, DiscoveryAnswer>()
      data.answers.forEach(a => answersMap.set(a.questionNumber, a))

      set({
        session: data.session,
        messages: localMessages,
        answers: answersMap,
        sources: data.sources,
        isLoading: false,
      })

      // If no messages, add initial greeting
      if (localMessages.length === 0) {
        const { workflowSteps } = get()
        // Use workflow step if loaded, otherwise fallback to DISCOVERY_QUESTIONS
        const firstStep = workflowSteps.length > 0 ? workflowSteps[0] : null
        const questionText = firstStep?.name || DISCOVERY_QUESTIONS[0].question
        get().addLocalMessage({
          role: 'assistant',
          content: `Welcome! I'm your discovery coach. Let's work through 12 key questions to validate your idea.\n\nWe'll start with Market Reality.\n\n**Question 1:** ${questionText}`,
          messageType: 'question',
          questionNumber: '1',
        })
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to load session', isLoading: false })
    }
  },

  createSession: async (accountId, projectId, data) => {
    set({ isLoading: true, error: null })
    try {
      const session = await discoveryApi.createSession(accountId, projectId, data)
      set({
        session,
        messages: [],
        answers: new Map(),
        sources: [],
        isLoading: false,
      })

      // Add initial greeting
      const { workflowSteps } = get()
      const firstStep = workflowSteps.length > 0 ? workflowSteps[0] : null
      const questionText = firstStep?.name || DISCOVERY_QUESTIONS[0].question
      get().addLocalMessage({
        role: 'assistant',
        content: data.ideaText
          ? `Thanks for sharing your idea! Let me ask you some questions to help validate it.\n\n**Question 1:** ${questionText}`
          : `Welcome! I'm your discovery coach. Let's work through 12 key questions to validate your idea.\n\nWe'll start with Market Reality.\n\n**Question 1:** ${questionText}`,
        messageType: 'question',
        questionNumber: '1',
      })

      return session
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to create session', isLoading: false })
      throw error
    }
  },

  sendMessage: async (accountId, projectId, content) => {
    const { session } = get()
    if (!session) return

    set({ isSending: true, error: null })

    // Add user message locally immediately
    get().addLocalMessage({
      role: 'user',
      content,
      messageType: 'answer',
      questionNumber: session.currentQuestion,
    })

    try {
      // Save to API
      await discoveryApi.addMessage(accountId, projectId, session.id, {
        role: 'user',
        content,
        messageType: 'answer',
        questionNumber: session.currentQuestion,
      })

      set({ isSending: false, inputText: '' })
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to send message', isSending: false })
    }
  },

  saveAnswer: async (accountId, projectId, questionNumber, answer, quality) => {
    const { session } = get()
    if (!session) return

    try {
      const savedAnswer = await discoveryApi.saveAnswer(accountId, projectId, session.id, {
        questionNumber,
        answer,
        qualityScore: quality,
        confidencePercent: quality ? String(QUALITY_CONFIG[quality].confidence) : undefined,
      })

      set((state) => {
        const newAnswers = new Map(state.answers)
        newAnswers.set(questionNumber, savedAnswer)
        return { answers: newAnswers }
      })
    } catch (error) {
      console.error('Failed to save answer:', error)
    }
  },

  advanceToNext: async (accountId, projectId) => {
    const { session, workflowSteps, workflowPhases } = get()
    if (!session) return false

    try {
      const result = await discoveryApi.advanceSession(accountId, projectId, session.id)
      set({ session: result.session })

      if (!result.isComplete) {
        // Add transition message
        const nextQ = parseInt(result.session.currentQuestion)

        // Use workflow step if available, otherwise fallback to DISCOVERY_QUESTIONS
        const step = workflowSteps.find(s => s.order === nextQ)
        const questionText = step?.name || DISCOVERY_QUESTIONS[nextQ - 1]?.question || 'Next question'

        // Get phase info from workflow phases or fallback
        const phaseInfo = workflowPhases.find(p => p.key === result.session.currentPhase) ||
          PHASE_CONFIG[result.session.currentPhase]

        // Check if phase changed
        const prevQ = parseInt(session.currentQuestion)
        const prevStep = workflowSteps.find(s => s.order === prevQ)
        const prevPhase = prevStep?.phase || DISCOVERY_QUESTIONS[prevQ - 1]?.phase
        const phaseChanged = prevPhase !== result.session.currentPhase

        if (phaseChanged) {
          get().addLocalMessage({
            role: 'assistant',
            content: `Great progress! We're moving to **${phaseInfo.label}**.\n\n**Question ${nextQ}:** ${questionText}`,
            messageType: 'transition',
            questionNumber: String(nextQ),
          })
        } else {
          get().addLocalMessage({
            role: 'assistant',
            content: `**Question ${nextQ}:** ${questionText}`,
            messageType: 'question',
            questionNumber: String(nextQ),
          })
        }
      } else {
        get().addLocalMessage({
          role: 'assistant',
          content: `Congratulations! You've completed all 12 discovery questions. Let me summarize what we've learned...`,
          messageType: 'transition',
        })
      }

      return result.isComplete
    } catch (error) {
      console.error('Failed to advance:', error)
      return false
    }
  },

  addSource: async (accountId, projectId, source) => {
    const { session } = get()
    if (!session) return

    try {
      const savedSource = await discoveryApi.addSource(accountId, projectId, session.id, source)
      set((state) => ({
        sources: [...state.sources, savedSource],
      }))
    } catch (error) {
      console.error('Failed to add source:', error)
    }
  },

  // Local actions
  addLocalMessage: (message) => {
    const newMessage: LocalMessage = {
      ...message,
      id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
      timestamp: new Date(),
    }

    // Auto-detect poll data in assistant messages
    if (message.role === 'assistant' && !message.pollData && !message.multiStepData) {
      const pollData = parsePollFromContent(message.content)
      if (pollData) {
        newMessage.messageType = 'poll'
        newMessage.pollData = pollData
        // Strip JSON block from content for cleaner display
        newMessage.content = stripInteractiveBlocks(message.content)
      }

      const multiStepData = parseMultiStepFromContent(message.content)
      if (multiStepData) {
        newMessage.messageType = 'multi_step_question'
        newMessage.multiStepData = multiStepData
        // Strip JSON block from content
        newMessage.content = stripInteractiveBlocks(message.content)
      }
    }

    set((state) => ({
      messages: [...state.messages, newMessage],
    }))
  },

  updateStreamingMessage: (content) => {
    set((state) => {
      const messages = [...state.messages]
      const lastMsg = messages[messages.length - 1]
      if (lastMsg?.isStreaming) {
        messages[messages.length - 1] = { ...lastMsg, content }
      }
      return { messages }
    })
  },

  clearSession: () => {
    set({
      session: null,
      messages: [],
      answers: new Map(),
      sources: [],
      inputText: '',
      validation: {
        isValidating: false,
        assessment: null,
        suggestions: [],
        ghostText: null,
      },
      error: null,
    })
  },

  // Poll actions
  submitPollAnswer: async (accountId, projectId, pollId, selectedValues, otherText) => {
    const { session } = get()
    if (!session) return

    set({ isSending: true })

    try {
      // 1. Find poll message and update it locally
      const messages = [...get().messages]
      const pollMessage = messages.find(m => m.pollData?.pollId === pollId)
      if (pollMessage && pollMessage.pollData) {
        pollMessage.pollData.selectedValues = selectedValues
        if (otherText) pollMessage.pollData.otherText = otherText
        set({ messages })
      }

      // 2. Create user answer message showing selections
      const answerText = selectedValues.map(v => {
        const option = pollMessage?.pollData?.options.find(o => o.id === v)
        return option?.label || v
      }).join(', ') + (otherText ? `: ${otherText}` : '')

      get().addLocalMessage({
        role: 'user',
        content: answerText,
        messageType: 'answer',
        questionNumber: session.currentQuestion,
      })

      // 3. Save to API
      await discoveryApi.addMessage(accountId, projectId, session.id, {
        role: 'user',
        content: answerText,
        messageType: 'answer',
        questionNumber: session.currentQuestion,
        metadata: JSON.stringify({ pollId, selectedValues, otherText }),
      })

      // 4. Save as structured answer
      await get().saveAnswer(accountId, projectId, session.currentQuestion, answerText)

      set({ isSending: false })
    } catch (error) {
      console.error('Failed to submit poll answer:', error)
      set({ isSending: false, error: error instanceof Error ? error.message : 'Failed to submit poll' })
    }
  },

  submitMultiStepAnswer: async (accountId, projectId, multiStepId, allAnswers) => {
    const { session } = get()
    if (!session) return

    set({ isSending: true })

    try {
      // Find multi-step message
      const messages = [...get().messages]
      const multiStepMessage = messages.find(m => m.multiStepData?.multiStepId === multiStepId)

      // Create summary of all answers
      const answerSummary = Object.entries(allAnswers).map(([stepId, values]) => {
        const step = multiStepMessage?.multiStepData?.steps.find(s => s.id === stepId)
        if (!step) return ''

        const selectedLabels = values.map(v => {
          const option = step.options.find(o => o.id === v)
          return option?.label || v
        }).join(', ')

        return `${step.label}: ${selectedLabels}`
      }).join('\n')

      // Add user message
      get().addLocalMessage({
        role: 'user',
        content: answerSummary,
        messageType: 'answer',
        questionNumber: session.currentQuestion,
      })

      // Save to API
      await discoveryApi.addMessage(accountId, projectId, session.id, {
        role: 'user',
        content: answerSummary,
        messageType: 'answer',
        questionNumber: session.currentQuestion,
        metadata: JSON.stringify({ multiStepId, allAnswers }),
      })

      // Save as answer
      await get().saveAnswer(accountId, projectId, session.currentQuestion, answerSummary)

      set({ isSending: false })
    } catch (error) {
      console.error('Failed to submit multi-step answer:', error)
      set({ isSending: false, error: error instanceof Error ? error.message : 'Failed to submit' })
    }
  },

  // Coach workflow actions
  setCoachWorkflowEnabled: (enabled) => {
    set((state) => ({
      coachWorkflow: { ...state.coachWorkflow, isEnabled: enabled },
    }))
  },

  updateCoachWorkflowState: (update) => {
    set((state) => ({
      coachWorkflow: { ...state.coachWorkflow, ...update },
    }))
  },

  setCoachStreamingText: (text) => {
    set((state) => ({
      coachWorkflow: { ...state.coachWorkflow, streamingText: text },
    }))
  },

  setCoachAssessment: (assessment) => {
    set((state) => ({
      coachWorkflow: { ...state.coachWorkflow, lastAssessment: assessment },
    }))
  },

  addCoachMCQOption: (option) => {
    set((state) => ({
      coachWorkflow: {
        ...state.coachWorkflow,
        lastMCQOptions: [...(state.coachWorkflow.lastMCQOptions || []), option],
      },
    }))
  },

  clearCoachMCQOptions: () => {
    set((state) => ({
      coachWorkflow: { ...state.coachWorkflow, lastMCQOptions: [] },
    }))
  },

  setCoachExtraction: (extraction) => {
    set((state) => ({
      coachWorkflow: { ...state.coachWorkflow, lastExtraction: extraction },
    }))
  },

  advanceCoachStep: (nextStepKey, nextStepNumber) => {
    set((state) => ({
      coachWorkflow: {
        ...state.coachWorkflow,
        currentStepKey: nextStepKey,
        currentStepNumber: nextStepNumber,
        completedSteps: [...state.coachWorkflow.completedSteps, state.coachWorkflow.currentStepKey],
        lastAssessment: undefined,
        lastMCQOptions: [],
        lastExtraction: undefined,
        streamingText: '',
        phase: 'idle',
      },
    }))
  },

  resetCoachWorkflow: () => {
    set({
      coachWorkflow: {
        isEnabled: false,
        currentStepKey: 'understanding-context',
        currentStepNumber: 1,
        phase: 'idle',
        completedSteps: [],
        streamingText: '',
      },
    })
  },
}))

// Selector hooks for common data
export const useCurrentQuestion = () => {
  const session = useDiscoveryAssistantStore((state) => state.session)
  if (!session) return null
  const qNum = parseInt(session.currentQuestion)
  return DISCOVERY_QUESTIONS[qNum - 1] || null
}

export const useCurrentPhase = () => {
  const session = useDiscoveryAssistantStore((state) => state.session)
  if (!session) return null
  return PHASE_CONFIG[session.currentPhase] || null
}

export const useProgress = () => {
  const session = useDiscoveryAssistantStore((state) => state.session)
  if (!session) return { completed: 0, total: 12, percentage: 0 }
  const completed = session.completedQuestions.length
  return {
    completed,
    total: 12,
    percentage: Math.round((completed / 12) * 100),
  }
}
